// Desktop-notification bridge for the in-app notifications list.
//
// Mounted ONCE at AuthedLayout level (App.tsx) so it runs on every
// authed surface — not per-route, otherwise navigating between tabs
// would re-mount the hook and reset the "what have I already seen"
// state, leading to a fresh batch of beeps on every navigation.
//
// What it does:
//   1. Polls the same `useNotifications(50)` list the bell uses.
//   2. On mount, snapshots the current latest-seen timestamp so the
//      user doesn't get blasted with one banner per historical row.
//   3. On each refetch, looks for rows newer than the snapshot. If
//      the user has enabled desktop notifications AND granted browser
//      permission AND the tab is currently hidden, spawn a banner +
//      (optionally) play the chime. Mark each as already-spawned so
//      the same row doesn't fire twice on the next 60s poll.
//
// Why we DON'T gate on document.hidden anymore:
//   The original cut suppressed banners while the SPA tab was active,
//   on the theory that an active user can see the bell badge already.
//   In practice this made the feature feel broken during testing —
//   you'd flip the toggle, leave the tab focused, and never see a
//   single banner. It also failed the "multitasking across many tabs
//   on one window" case where the SPA is technically visible (no
//   `document.hidden = true`) but the user is looking at one of three
//   side-by-side windows. The browser + OS already de-duplicate
//   tab-focused-vs-not by not stealing focus; we don't need to gate
//   here. If an agent wants the "only when backgrounded" behaviour
//   back, add a pref toggle in `notification-prefs.ts` and an opt-in
//   check below.
//
// Permission UX:
//   We do NOT auto-prompt for Notification permission on app load.
//   That gets blocked by Chrome's permission-prompt heuristics and
//   trains users to dismiss the bar. Instead the user explicitly
//   clicks "Enable notifications" — in the banner under the nav
//   (DesktopNotificationsBanner) or the Settings card — which calls
//   `Notification.requestPermission()` from a user gesture handler.

import { useEffect, useRef } from 'react';
import {
  useNotifications,
  type Notification as PaNotification,
  type NotificationType,
} from '@/lib/queries';
import { useNotificationPrefs } from '@/lib/notification-prefs';
import { useCompanyBranding } from '@/lib/queries';
import { playChime } from '@/lib/chime';

// Human-friendly notification-type → banner-title prefix. Keeps the
// banner readable at a glance ("Client replied · Order shipping
// query") instead of dumping the raw event_type. Mirrors the icon
// mapping in NotificationsBell but uses words instead of icons.
const TITLE_PREFIX: Partial<Record<NotificationType, string>> = {
  client_replied: 'Client replied',
  sla_breach: 'SLA breach',
  follow_up_sent: 'Follow-up sent',
  follow_up_reminder: 'Follow-up reminder',
  reassigned_to_you: 'Reassigned to you',
  new_ticket_assigned: 'New ticket assigned',
  ticket_escalated: 'Escalated',
  ticket_resolved: 'Resolved',
  note_added: 'New note',
  note_mention: 'You were mentioned',
  added_as_watcher: 'Added as watcher',
  leave_requested: 'Leave requested',
  leave_decided: 'Leave decision',
  leave_approved_team: 'Team leave approved',
  leave_reminder: 'Upcoming leave',
};

function formatTitle(n: PaNotification): string {
  const prefix = TITLE_PREFIX[n.type] ?? 'Notification';
  if (n.title && n.title.length > 0) return `${prefix} · ${n.title}`;
  return prefix;
}

export function useDesktopNotifications(): void {
  const { prefs } = useNotificationPrefs();
  const notifsQ = useNotifications(50);
  const brandingQ = useCompanyBranding();
  const logoUrl = brandingQ.data?.logo_url ?? null;

  // Snapshot of (a) the timestamp at which this hook first observed
  // the notifications list, and (b) every notification id we've
  // already spawned a banner for. Refs (not state) because we don't
  // want a re-render when these update — they're a side-channel for
  // the effect.
  const startedAt = useRef<number>(Date.now());
  const spawnedIds = useRef<Set<string>>(new Set());

  useEffect(() => {
    if (!notifsQ.data) return;
    if (!prefs.enabled) return;
    if (typeof window === 'undefined' || !('Notification' in window)) return;
    if (Notification.permission !== 'granted') return;

    const newOnes = notifsQ.data.filter((n) => {
      if (spawnedIds.current.has(n.id)) return false;
      const created = new Date(n.created_at).getTime();
      return created > startedAt.current;
    });

    if (newOnes.length === 0) return;

    for (const n of newOnes) {
      spawnedIds.current.add(n.id);
      try {
        const banner = new Notification(formatTitle(n), {
          body: (n.body ?? '').slice(0, 240),
          // `tag` = the row id so a re-fire on the same row replaces
          // rather than stacks (matches the assist hook's pattern).
          tag: `pa-notif-${n.id}`,
          icon: logoUrl ?? undefined,
          // Don't require interaction — short-lived banner so the user
          // can glance at it and get back to whatever they were doing.
        });
        // Clicking the banner brings the SPA tab back into focus +
        // navigates to the ticket if there is one. The banner closes
        // on click automatically; we just need to refocus + nav.
        banner.onclick = () => {
          try {
            window.focus();
            if (n.ticket_id) {
              window.location.assign(`/tickets/${n.ticket_id}`);
            }
          } catch {
            // ignore — some browsers throw on focus() from a banner click
          }
          banner.close();
        };
      } catch {
        // Some browsers throw if the page is in an inactive worker context.
      }
    }

    if (prefs.sound) playChime();
  }, [notifsQ.data, prefs.enabled, prefs.sound, logoUrl]);
}
